import { useNavigate } from 'react-router-dom'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { useMapDraft } from '../../hooks/use-map-draft'
import { Helpers } from '../../utils/helper'
import { openRouteStageD, openRouteStageE } from '../../redux/slices/routes-management-slice'
import { updateStageDApplicationStatus } from '../../redux/slices/application-stage-d-slice'
import { ApplicationDetail } from '../../models/classes/ApplicationDetail.class'
import { PolymerAxios } from '../../utils/axios'
import { P2Modal } from 'react-bootstrap-common-script'
import { setShowApplicationCancelled } from '../../redux/slices/application-cancelled-slice'
import { ApplicationStatusEnum, PolymerStatusEnum } from '../../models/enum/application-status.enum'
import { setApplicationId } from '../../redux/slices/application-id-slice'
import { API_ApplicationDelete, API_ApplicationDetail, API_DraftDetail } from '../../utils/api'
import useGetIpaLetter from '../../hooks/use-get-ipa-letter'
import { downloadFile } from '../../utils/download-file'
import Stages, { StagesEnum } from './stages/stages'
import { resetAuthData, updateStructureStatus } from '../../redux/slices/authentication-slice'
import { updateStageAStep1, updateStageAStep2, updateStageAStep3, updateStageAStep4, updateStageAStep5, updateStageAStepDeclaration } from '../../redux/slices/completed-steps-slice'
import useCheckSmdByHdbRef from '../../hooks/use-check-sms-by-hdb-ref'
import { LottieLoader } from '../loader'
import { OwnerConsentStatus } from '../../models/polymer/ConsentApplication.model'
import { PolymerCrypto } from '../../utils/encryptor'
import { setBackTriggered } from '../../redux/slices/back-triggered-slice'
import { StructureStatusEnum } from '../../models/enum/structure-status.enum'
import { HoldingTypeEnum } from '../../models/enum/holding-type.enum'
import { Properties } from '../../models/polymer/Properties.model'
import useGetEligibilityOverride from '../../hooks/use-get-eligibility-override'
import useCheckHdbProperties from '../../hooks/use-check-hdb-properties'

interface StagesFlowProps{
  activeStage?: StagesEnum,
  stagesFlowCheckingData?: ApplicationDetail;
  applicantConsentGiven: boolean | undefined;
  applicationStatus?: string;
  polymerStatus?: PolymerStatusEnum;
  consentApplicant?: OwnerConsentStatus[]
}

export interface StagesFlowPendingTask {
  title: string;
  type: string;
}

const stages = ["A", "B", "C", "D", "E"];

const getIsStartedAndCompletedAt = (stage: string, stagesFlowCheckingData: ApplicationDetail | undefined) => {
    let obj: any;
    switch(stage){
      case "A":
        obj = stagesFlowCheckingData?.stage_a;
        break;
      case "B":
        obj = stagesFlowCheckingData?.stage_b;
        break;
      case "C":
        obj = stagesFlowCheckingData?.stage_c;
        break;
      case "D":
        obj = stagesFlowCheckingData?.stage_d;
        break;
      case "E":
        obj = stagesFlowCheckingData?.stage_e;
        break;
      default:
        obj = undefined;
        break;
    }

    return {
       isStarted: obj?.is_started ?? false,
       completedAt: obj?.completed_at ?? ""
    }

}
function StagesFlow({stagesFlowCheckingData, ...props}:StagesFlowProps) {
  let navigate = useNavigate();
  let AppDispatch = useAppDispatch();
  const queryParameters = new URLSearchParams(window.location.search)
  const application_id = queryParameters.get("application_id");
  const application_status = useAppSelector(state => state.app.ApplicationStageDSlice.application_status);
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const applicationStartFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const applicationStep3FormData = useAppSelector(state => state.app.ApplicationStep3Slice.formData);
  const applicationStep4FormData = useAppSelector(state => state.app.ApplicationStep4Slice.formData);
  const properties = useAppSelector(state => state.app.GenuinePolymerSlice.properties);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const { consentBypassNrics } = useGetEligibilityOverride(applicationStartFormData.selected_hdb_property?.hdb_ref);


  const [ isDataMappingLoading, setDataMappingIsLoading ] = useState<boolean>(true);

  const { ipa_key } = useGetIpaLetter(application_status, props.polymerStatus);
  const [cancelModalVisible, setCancelModalVisible] = useState<boolean>(false);
  
  const [ applicationData, setApplicationData ] = useState<any>();
  const { HdbPropertyData, isLoading: isRdbLoading, isError: isRdbError } = useCheckHdbProperties(applicationData?.main_applicant_uinfin ? PolymerCrypto.decryption(applicationData?.main_applicant_uinfin) : "", props.activeStage ?? "A");
  const { smsInfo, hasOutstandingFees, isLoading: isSmsLoading, setHdbRefValueFunction, isError } = useCheckSmdByHdbRef(application_id ? applicationData?.hdb_ref : "", props.activeStage);
  const { performDraftMapping } = useMapDraft();

  const deathApplicantUinfins = useMemo(()=>{
    const uinfins:string[] = [];
    applicationStartFormData?.selected_hdb_property?.owners?.forEach(e => {
      if(e.live_status === "D" && e?.uinfin){
        uinfins.push(e?.uinfin);
      }
    })
    return uinfins;
  },[applicationStartFormData])


  const getDraft = async () => {
    return await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(props.applicationStatus === "SAVE_DRAFT" ? `${API_DraftDetail}?applicationUuid=${application_id}` : `${API_ApplicationDetail}?applicationUuid=${application_id}`).then(res => {
      if (res.status === 200) {
        setApplicationData(res.data);
        setHdbRefValueFunction(res.data.hdb_ref)
      }
      else if(res.status === 401){
        AppDispatch(resetAuthData());
      }
      else {
        return false;
      }
    }).catch(e => {
      console.log("Oops, something wrong, please try again later");
      navigate(-1);
      return false;
    })
  }

  useEffect(() => {
    if(props.applicationStatus) {
      getDraft();
    }
  }, [])
  useEffect(()=>{
    if(application_id){
      AppDispatch(setApplicationId(application_id));
    } else {
      setDataMappingIsLoading(false);
    }
  },[application_id])

  const loginUserStructureStatus = useMemo(() => {
    const checkOriginalOwner = applicationStep1FormData.household.owners.find(
      (item) => item.myinfo.uinfin === loginUser.uinfin
    );

    const checkRetainingOwner = applicationStep1FormData.household.incoming_owners.find(
      (item) => item.myinfo.uinfin === loginUser.uinfin
    );

    if(checkOriginalOwner && checkRetainingOwner){
      return StructureStatusEnum.retaining;
    }else if(checkOriginalOwner && !checkRetainingOwner){
      return StructureStatusEnum.outgoing;
    }else{
      return StructureStatusEnum.incoming;
    }

  }, [loginUser, applicationStep1FormData]);

  useEffect(() => {
    AppDispatch(updateStructureStatus(loginUserStructureStatus));
  }, [loginUserStructureStatus])

  const updateStartApplication = useCallback(()=>{
    // AppDispatch(updateFormData(new ApplicationStart({...applicationStartFormData, selected_hdb_property:{ ...applicationStartFormData.selected_hdb_property, ...smsInfo }})))
    console.log("sms info:" , smsInfo);
    let property = (HdbPropertyData ?? []).find(item => item.hdb_ref === applicationData.hdb_ref);
    if(property){
      property = new Properties({...property});
      property.decryptOwnersOccupiersAdditionalRoles();
    }
    performDraftMapping({...applicationData}, false, smsInfo, property, hasOutstandingFees);
    setDataMappingIsLoading(false);
  },[applicationData, performDraftMapping, smsInfo, properties, HdbPropertyData, hasOutstandingFees])

  //NOTE: this useEffect is for non NEW application.
  useEffect(()=>{
    if(isError || isRdbError) {
      alert("An error occurred. Please try again later.");
      window.location.href = "/transfer";
      return;
    }

    let checkRdbDataComplete = true;
    if(props.activeStage === "A") {//only A need to use
       checkRdbDataComplete = !isRdbLoading && HdbPropertyData !== undefined;
    }
    if(!isSmsLoading && props.applicationStatus && applicationData && smsInfo && checkRdbDataComplete){
      console.log('updating start application')
      updateStartApplication();
    }
  },[AppDispatch, isSmsLoading, smsInfo, applicationData, updateStartApplication, isError, isRdbLoading, HdbPropertyData, isRdbError, props.activeStage])

  const canCancelApplication = useMemo(() => {
      if(applicationStep1FormData.reason.registrar_uinfin && loginUser?.uinfin && applicationStep1FormData.reason.registrar_uinfin === loginUser?.uinfin){
          return false;
      }

      return true;
  }, [applicationStep1FormData.reason.registrar_uinfin, loginUser?.uinfin])

  const stageDRouteChecking = useCallback(() =>{
    if(application_status === "SENT_TO_BIOME"){
      //D1
      return `/application-coo/stage-d/hdb-review?application_id=${application_id}`;
    }
    else if(props.polymerStatus){
      //NOTE: We checking using polymerStatus is because D2 and D3 next button will update the polymerStatus, So that they will go through all the D2 and D3 regardless of application status.
      // The scenario when prepare_for_completion status is assigned but applicant haven't completely go through D2 and D3 yet.

      if((props.polymerStatus === "D3") && (application_status === "REVIEW_FINANCIAL_PLAN" || application_status === "PREPARE_FOR_COMPLETION")){
        return `/application-coo/stage-d/review-financial-plan?application_id=${application_id}`;
      }
      else{
        return `/application-coo/stage-d/in-principle-approval?application_id=${application_id}`;
      }
    }
    else if(application_status === "APPROVE"){//In case polymerStatus is empty when in STAGE D, then use back application_status
      return `/application-coo/stage-d/in-principle-approval?application_id=${application_id}`;
    }
    else if(application_status === "REVIEW_FINANCIAL_PLAN"){
      return `/application-coo/stage-d/review-financial-plan?application_id=${application_id}`;
    }
    else{
      return `?application_id=${application_id}`;
    }
  },[application_id, application_status, props.polymerStatus])

  const performStageCompleteUpdate = useCallback((step: number) => {
    if(step > 1){
      AppDispatch(updateStageAStep1(true));
    }

    if(step > 2){
      AppDispatch(updateStageAStep2(true));
    }

    if(step > 3){
      AppDispatch(updateStageAStep3(true));
    }

    if(step > 4){
      AppDispatch(updateStageAStep4(true));
    }
    
    if(step > 5){
      AppDispatch(updateStageAStep5(true));
    }
  }, [AppDispatch])

  const checkOngoingStep3 = useMemo(() => {
      if(applicationStep3FormData.holding_type && applicationStep3FormData.holding_type !== HoldingTypeEnum.sole_ownership){
          return true;
      }

      if(applicationStep3FormData.current_solicitor_type){
          return true;
      }

      if(applicationStep3FormData.proposed_solicitor_type){
         return true;
      }

      return false;

  }, [applicationStep3FormData])

  const mainApplicantCompletedStep5 = useMemo(()=>{
    return applicationStep1FormData.household.owners.some( e => e.myinfo.email || e.myinfo.mobile_no ) || applicationStep1FormData.household.incoming_owners.some( e => e.myinfo.email || e.myinfo.mobile_no ) || applicationStep1FormData.household.occupiers.some( e => e.myinfo.email || e.myinfo.mobile_no ) || (applicationData && applicationData.owners.some( (e: { email: any; mobile_no: any }) => e.email || e.mobile_no ))
  },[applicationStep1FormData.household.incoming_owners, applicationStep1FormData.household.occupiers, applicationStep1FormData.household.owners, applicationData]);

  const stageAViewApplication = useCallback(()=>{
    if(props.activeStage !== "A"){
      //When active stage is not "A", mean the user have completed the Stage A and will be redirect the "Review summary" page.
      navigate('/application-coo/stage-a/view-application');
    }
    else if(stagesFlowCheckingData?.application_id){
      AppDispatch(setBackTriggered(false));
      AppDispatch(setApplicationId(stagesFlowCheckingData?.application_id ?? undefined));
      AppDispatch(updateStageAStepDeclaration(stagesFlowCheckingData?.application_id !== undefined));
      const incomingOwners = applicationStep1FormData.household.incoming_owners;
      const isNotOutgoing = incomingOwners.some(item => item.myinfo.uinfin === loginUser.uinfin);
      //check which of the steps below is done halfway and go to respective page
      if(mainApplicantCompletedStep5){
        performStageCompleteUpdate(5);
        navigate("/application-coo/stage-a/summary");
      }
      else if(applicationStep4FormData.file_items.length > 0 || applicationStep4FormData.excepted_documents.length > 0){
        performStageCompleteUpdate(4);
        navigate("/application-coo/stage-a/upload-documents");
      }else if(checkOngoingStep3 === true || (applicationStep2FormData.new_loan_type && !isNotOutgoing)){
        performStageCompleteUpdate(3);
        navigate("/application-coo/stage-a/conveyancing");
      }else if(applicationStep2FormData.new_loan_type){
        performStageCompleteUpdate(2);
        navigate("/application-coo/stage-a/financing");
      }else{
        navigate("/application-coo/stage-a/eligibility");
      }
    }else {
      AppDispatch(setApplicationId(undefined));
      navigate('/application-coo/stage-a/declaration');
    }
  },[props.activeStage, stagesFlowCheckingData?.application_id, navigate, AppDispatch, applicationStep1FormData.household.incoming_owners, applicationStep4FormData.file_items.length, checkOngoingStep3, applicationStep2FormData.new_loan_type, loginUser.uinfin, performStageCompleteUpdate]);

  const stageBViewApplication = useCallback(()=>{
    AppDispatch(setApplicationId(stagesFlowCheckingData?.application_id ?? undefined));
    if(props.applicantConsentGiven !== undefined){
      if(props.applicantConsentGiven || consentBypassNrics.includes(loginUser.uinfin) || !canCancelApplication || props.applicationStatus !== "SUBMIT_COMPLETE"){
        navigate(`/application-coo/stage-b/consent?application_id=${application_id}`);
      } else {
        navigate(`/application-coo/stage-b/review?application_id=${application_id}`);
      }
    }
    else{
      console.log("is checking applicant consent api....")
    }
  },[AppDispatch, stagesFlowCheckingData?.application_id, props.applicantConsentGiven, navigate, application_id, canCancelApplication, props.applicationStatus]);
  
  const stageCViewApplication = useCallback(()=>{
    if(props.activeStage ==="D" || props.activeStage === "E"){
      navigate(`/application-coo/stage-c/payment/successful?application_id=${application_id}`);
    }
    else{
      navigate(`/application-coo/stage-c/payment?application_id=${application_id}`);
    }
  },[application_id, navigate, props.activeStage]);

  const stageDViewApplication = useCallback(()=>{
    console.log("DEBUG - STAGE D", props.polymerStatus);

    if(props.polymerStatus === "E" && application_status === "PREPARE_FOR_COMPLETION"){
      //mean will show "VIEW IPA LETTER BUTTON"
      if(ipa_key){
        downloadFile(ipa_key, singpassToken);
      }
      else{
        alert("Oops, ipa letter not found");
      }
    }
    else{
      navigate(stageDRouteChecking());
    }
  },[application_status, navigate, props.polymerStatus, stageDRouteChecking, ipa_key, singpassToken]);
  
  const stageEViewApplication = useCallback(()=>{
    // alert("Stage E")
    navigate(`/application-coo/stage-e/prepare-for-completion?application_id=${application_id}`)
  },[application_id, navigate]);


  const StagesCTA = useCallback((stage: string)=> () =>{
    if(stage === "A"){
      stageAViewApplication();
    }
    else if(stage === "B"){
      stageBViewApplication();
    }
    else if(stage === "C"){
      stageCViewApplication();
    }
    else if(stage === "D"){
      stageDViewApplication();
    }
    else if(stage === "E"){
      stageEViewApplication();
    }
    else{
      alert("stagesCTA - " + stage);
    }
  },[stageAViewApplication, stageBViewApplication, stageCViewApplication, stageDViewApplication, stageEViewApplication])

  const isPaymentRequired = useMemo(() => {
    const paymentNotRequiredStatuses: ApplicationStatusEnum[] = [
      "SAVE_DRAFT",
      "SUBMIT_COMPLETE",
      "GATHER_CONSENT_COMPLETE",
      "PENDING_PAYMENT",
      "PAYMENT_COMPLETE",
      "SENT_TO_BIOME",
      "PENDING_APPLICANT_HDB_REVIEW",
      "APPROVE",
    ];

    if(stagesFlowCheckingData?.application_status){
      if(!paymentNotRequiredStatuses.includes(stagesFlowCheckingData?.application_status)){
          return true;
      }else{
          return false;
      }
    }
    return false;
  }, [stagesFlowCheckingData])
  
  const cancelCTA = useCallback(() =>{
      setCancelModalVisible(true);
  }, [])

  const cancelApplication = useCallback(async() => {
    try{
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(`${API_ApplicationDelete}`, {application_uuid: application_id});
      AppDispatch(setShowApplicationCancelled(true));
      navigate('/');
    }catch(error){
      console.error(error);
      alert("Opps, something wrong when cancelling the application");
    }
    setCancelModalVisible(false);
  }, [AppDispatch, application_id, loginUser?.uinfin, navigate, singpassToken])


  /**
   * process application_status and open route based on the status
   */
  useEffect(()=>{
    if(stagesFlowCheckingData?.application_status){
      switch(stagesFlowCheckingData.application_status){
        case "SENT_TO_BIOME":
        case "PENDING_APPLICANT_HDB_REVIEW":
        case "HDB_REVIEW":
        case "APPROVE":
        case "PENDING_BOOKING_APPOINTMENT":
        case "REVIEW_FINANCIAL_PLAN":
          AppDispatch(openRouteStageD(true));
          AppDispatch(updateStageDApplicationStatus(stagesFlowCheckingData.application_status));
          break;
        case "PREPARE_FOR_COMPLETION":
          if(props.polymerStatus !== "E"){
            AppDispatch(openRouteStageD(true));
            AppDispatch(updateStageDApplicationStatus(stagesFlowCheckingData.application_status));
          }
          AppDispatch(openRouteStageE(true));
        break;
      }
    }
  },[AppDispatch, stagesFlowCheckingData])

  useEffect(() => {
      AppDispatch(updateStageDApplicationStatus(stagesFlowCheckingData?.application_status ?? ""));
  }, [stagesFlowCheckingData?.application_status])

  const actionsRequired = useMemo(():StagesFlowPendingTask[] => {
    let pendingTasks:StagesFlowPendingTask[] = [];
    try{//if any error will just return empty pending tasks
      switch(props.activeStage){
        case "A":
          pendingTasks = [
          {
            title: "Complete application form",
            type: "task",
          },
          {
            title: "Upload documents",
            type: "task",
          },
        ]

        if(stagesFlowCheckingData?.stage_a.draft_expires_at){
          pendingTasks.push({
            title: `Saved draft is valid until ${Helpers.formatDate(stagesFlowCheckingData?.stage_a.draft_expires_at, "short")}`,
            type: "remark",
          })
        }
          break;
        case "B":
          pendingTasks = [];
          props.consentApplicant!.filter(e=> !deathApplicantUinfins.includes(PolymerCrypto.decryption(e.uin_fin))).forEach((item: OwnerConsentStatus) => {
            if(item.consent_given === false){
              pendingTasks.push({
                title: `Consent from ${item.owner_name}`,
                type: "task"
              })
            }
          })
          if(stagesFlowCheckingData?.stage_b.deadline){
            pendingTasks.push({
              title: `Deadline on ${Helpers.formatDate(stagesFlowCheckingData?.stage_b.deadline, "short")}`,
              type: "remark"
            })
          }
          break;
        case "C":
          pendingTasks = [
            {title: "Make payment", type: "task"}, 
          ]
          if(stagesFlowCheckingData?.stage_c.deadline){
            pendingTasks.push({title: `Deadline on ${Helpers.formatDate(stagesFlowCheckingData?.stage_c.deadline, "short")}`, type: "remark"})
          }
          break;
        case "D":
          pendingTasks = [
            {title: "Re-submit documents", type: "task"}, 
            {title: "Endorse forms", type: "task"}, 
            {title: "Review financial plan", type: "task"}, 
            {title: "Schedule completion appointment", type: "task"}, 
          ]
          break;
        case "E":
          pendingTasks = [
            {title: "Prepare documents", type: "task"}, 
            {title: "Ensure all owners attend in person", type: "task"}, 
          ]
          if(stagesFlowCheckingData?.stage_e.attend_completion_date){
            pendingTasks.push(  {title: `Attend completion on ${Helpers.formatDate(stagesFlowCheckingData?.stage_e.attend_completion_date ?? "", "short")}`, type: "remark"})
          }
          break;
        default:
          break;
      }
      return pendingTasks;
    }catch(error){
      return pendingTasks;
    }
  },[deathApplicantUinfins, props.activeStage, props.consentApplicant, stagesFlowCheckingData?.stage_a.draft_expires_at, stagesFlowCheckingData?.stage_b.deadline, stagesFlowCheckingData?.stage_c.deadline, stagesFlowCheckingData?.stage_e.attend_completion_date])

  const closeModal = useCallback(() => {
    setCancelModalVisible(false)
  }, []);

  const handleProceed = useCallback(async() => {
    if(isPaymentRequired){
      navigate(`/application-coo/cancel-application-payment?application_id=${application_id}`)
    }else{
      await cancelApplication();
    }
  },[application_id, cancelApplication, isPaymentRequired, navigate]);

  return (
    <>
      <div className='polymer-stages-flow'>
        {stages.map((item) => {
          return <Fragment key={item}><Stages applicationId={application_id ?? undefined} stage={item as StagesEnum} activeStage={props.activeStage} stageCTA={StagesCTA} cancelCTA={cancelCTA} pendingTasks={props.activeStage === item ? actionsRequired : []} isStarted={getIsStartedAndCompletedAt(item, stagesFlowCheckingData).isStarted} completedAt={getIsStartedAndCompletedAt(item, stagesFlowCheckingData).completedAt} applicationSubmittedAt={stagesFlowCheckingData?.stage_a.completed_at} paymentCompletedAt={stagesFlowCheckingData?.stage_c.completed_at} canCancelApplication={canCancelApplication} /></Fragment>
        })}
      </div>
      <P2Modal 
          isOpen={isSmsLoading || isDataMappingLoading}
          children={<LottieLoader/>}
      />
      <P2Modal
          className="polymer-cancel-application-modal"
          isOpen={cancelModalVisible}
          onCloseModal={closeModal}
          variant="default"
          title={`Are you sure you want to cancel your application?`}
          primaryButtonText="Yes, cancel current application"
          secondaryButtonText="No, continue current application"
          primaryButtonFunction={handleProceed}
          secondaryButtonFunction={closeModal}
          children={
            !isPaymentRequired ? (
              <p style={{marginBottom: 32}}>
                Upon confirmation of the cancellation, HDB will cease processing
                your application, and all applicants will be notified of the
                cancellation. All fees paid are non-refundable.
              </p>
            ) : (
              <div style={{marginBottom: 32}}>
                <p>
                  Your application has been approved earlier and is currently
                  being processed for legal conveyancing. If you cancel the
                  application now, you will be liable for half of the conveyancing
                  fees and all disbursements incurred.
                </p>
                <br />
                <p>
                  Once you have made payment, HDB will cease the processing of
                  your application, and all applicants will be notified of the
                  cancellation. All fees paid are non-refundable.
                </p>
              </div>
            )
          }
        />
    </>

  )
}

export default StagesFlow